import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { AUTOMATIONS_SKILL_NAME } from '#pplx/frontend/apps/ask/automations-shared/constants.js';
import { getSearchModelForQModel } from '#pplx/frontend/apps/ask/hooks/query-fill-params-hooks/internal/getSearchModelForQModel.js';
import { getSourcesForQSources } from '#pplx/frontend/apps/ask/hooks/query-fill-params-hooks/internal/getSourcesForQSources.js';
import { mergeSourceSuggestionParam } from '#pplx/frontend/apps/ask/hooks/query-fill-params-hooks/internal/mergeSourceSuggestionParam.js';
import { setQFillAttribution } from '#pplx/frontend/apps/ask/hooks/query-fill-params-hooks/qfillAttribution.js';
import { getSearchModeForQMode } from '#pplx/frontend/apps/ask/hooks/query-fill-params-hooks/searchModeQMode.js';
import { useCurrentResultIds } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { updateSearchParams } from '#pplx/frontend/apps/ask/routing/historySearchParams.js';
import { usePreferredSearchModelForSearchMode } from '#pplx/frontend/apps/ask/search/hooks/usePreferredSearchModelForSearchMode.js';
import { getSkillDisplayName } from '#pplx/frontend/apps/ask/skill-cards/skillDisplay.js';
import { getSelectableSkillById } from '#pplx/frontend/apps/ask/skills/hooks/getSelectableSkillById.js';
import { buildSkillLinkPrefill, buildSkillPrefill, createPluginMentionNode, createQueryPrefillEditorState, createQueryPrefillTextNode, } from '#pplx/frontend/apps/ask/skills/utils/buildSkillQueryPrefill.js';
import { parseSkillInstructionPrefix } from '#pplx/frontend/apps/ask/skills/utils/skillInstruction.js';
import { fetchSource } from '#pplx/frontend/apps/ask/sources/hooks/fetchSource.js';
import { ASK_INPUT_PREFILL_PENDING_EVENT, ASK_INPUT_PREFILL_LANDING_PATHS, getPendingAskInputPrefill, takePendingAskInputPrefill, } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { isValidHttpUrl } from '#pplx/frontend/common/files/urls.js';
import { QMODE_PARAM, QFILL_PARAM, QSKILLS_PARAM, QSOURCE_SUGGESTIONS_PARAM, QSOURCES_PARAM, QSOURCES_REFRESH_PARAM, Q_PARAM, QMODEL_PARAM, QMODEL_ID_PARAM, QATTACH_PARAM, QATTACH_NAME_PARAM, QREMIX_PARAM, QRELATED_QUERY_UUID_PARAM, } from '#pplx/frontend/components/utils/consts.js';
import { serializedStateToPlainText } from '#pplx/frontend/composer-sdk/serializedStateToPlainText.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
const DEEP_RESEARCH_SKILL_NAME = 'deep-research';
const DEEP_RESEARCH_QSKILL_ALIAS = 'research';
const MODEL_COUNCIL_SKILL_NAME = 'model-council';
const CONTROL_LOCAL_BROWSER_SKILL_NAME = 'control-local-browser';
const IMMEDIATE_SWALLOWED_PARAMS = [
    QFILL_PARAM,
    Q_PARAM,
    QSKILLS_PARAM,
    QSOURCES_PARAM,
    QSOURCES_REFRESH_PARAM,
    QATTACH_PARAM,
    QATTACH_NAME_PARAM,
    QREMIX_PARAM,
    QRELATED_QUERY_UUID_PARAM,
];
const LOADED_SWALLOWED_PARAMS = [QMODE_PARAM, QMODEL_PARAM, QMODEL_ID_PARAM];
function createPrefillMentionNode(mention) {
    if (mention.variant === 'plugin')
        return createPluginMentionNode(mention);
    return {
        props: {
            uuid: mention.sourceId,
            variant: 'sources',
            text: mention.displayName,
            icon: mention.icon,
        },
        type: 'mention',
        version: 1,
    };
}
function buildPendingAskInputPrefill(prefill) {
    const children = [
        ...prefill.mentions.flatMap((mention) => [
            createPrefillMentionNode(mention),
            createQueryPrefillTextNode(' '),
        ]),
        createQueryPrefillTextNode(prefill.value),
    ];
    const json = createQueryPrefillEditorState(children);
    return { json, value: serializedStateToPlainText(json) };
}
function getQSkillIds(skillsValue) {
    if (!skillsValue) {
        return [];
    }
    return skillsValue
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .map((value) => {
        const normalizedValue = value.toLowerCase();
        if (normalizedValue === DEEP_RESEARCH_QSKILL_ALIAS) {
            return DEEP_RESEARCH_SKILL_NAME;
        }
        if (normalizedValue === AUTOMATIONS_SKILL_NAME ||
            normalizedValue === CONTROL_LOCAL_BROWSER_SKILL_NAME ||
            normalizedValue === DEEP_RESEARCH_SKILL_NAME ||
            normalizedValue === MODEL_COUNCIL_SKILL_NAME) {
            return normalizedValue;
        }
        return value;
    });
}
function getNamedQSkillName(skillId) {
    if (skillId === AUTOMATIONS_SKILL_NAME ||
        skillId === CONTROL_LOCAL_BROWSER_SKILL_NAME ||
        skillId === DEEP_RESEARCH_SKILL_NAME ||
        skillId === MODEL_COUNCIL_SKILL_NAME) {
        return skillId;
    }
    return undefined;
}
/**
 * Reads the `qfill` (or `q`), `qskills`, `qsources`, `qmode`, `qattach`, and `qremix` URL
 * parameters, applies them to the ask input state, then removes all from the URL.
 *
 * - `qfill` / `q`: Pre-fills the input text as plain text.
 * - `qskills`: Comma-separated skill IDs to pre-select. When `qfill` starts with a canonical
 *   skill instruction, its first ID is rendered as a skill mention. Skill links embedded directly
 *   in `qfill` render as visible mentions represented directly in the editor JSON.
 * - `qsources`: Comma-separated source IDs to pre-select (e.g. `scholar,slack`).
 * - `qmode`: A SearchMode value (e.g. `research`) to pre-select the search mode.
 * - `qattach`: URL of a file to attach to the input.
 * - `qattach_name`: Filename for the attached file.
 * - `qremix`: Session-scoped handoff id for a richer remix attachment.
 */
export function useQueryFillParams({ setUserInput, addAttachmentUrl, addRemixAttachment, }) {
    const query = useSearchParams();
    const pathname = usePathname();
    const queryClient = useQueryClient();
    const { openToast } = useToastContext();
    const { $t } = useIntl();
    const { currentThreadId } = useCurrentResultIds();
    const { configuredModel, setSources, setConfiguredModel } = useThreadConfig();
    const { isLoaded: isSearchModelsLoaded, getPreferredSearchModelForSearchMode } = usePreferredSearchModelForSearchMode();
    const applyQFill = useCallback(async (qfillValue, skillsValue, isCancelled) => {
        const skillIds = getQSkillIds(skillsValue);
        const linkedSkillPrefill = qfillValue ? buildSkillLinkPrefill(qfillValue) : null;
        if (linkedSkillPrefill) {
            setUserInput(linkedSkillPrefill.value, linkedSkillPrefill.json);
            return;
        }
        const parsedInstruction = qfillValue ? parseSkillInstructionPrefix(qfillValue) : null;
        const skillId = parsedInstruction
            ? skillIds[0]
            : skillIds.find((value) => getNamedQSkillName(value));
        if (skillId) {
            const namedSkill = getNamedQSkillName(skillId);
            let skillName;
            let localizedName;
            if (namedSkill) {
                skillName = namedSkill;
                if (namedSkill === CONTROL_LOCAL_BROWSER_SKILL_NAME) {
                    localizedName = $t({
                        defaultMessage: 'Control browser',
                        description: 'Display name for the Control browser Computer skill',
                        id: '91971e5441',
                    });
                }
                else if (namedSkill === DEEP_RESEARCH_SKILL_NAME) {
                    localizedName = $t({
                        defaultMessage: 'Deep Research',
                        description: 'Branded display name for the Deep Research skill',
                        id: 'a1a097df67',
                    });
                }
                else if (namedSkill === MODEL_COUNCIL_SKILL_NAME) {
                    localizedName = $t({
                        defaultMessage: 'Model Council',
                        description: 'Branded display name for the Model Council skill',
                        id: 'b863d2264c',
                    });
                }
                else {
                    localizedName = $t({
                        defaultMessage: 'Automations',
                        id: '6c7ff6302b',
                        description: 'Display name for the Automations Computer skill',
                    });
                }
            }
            else {
                const skill = await getSelectableSkillById(skillId, 'qfill-skill-resolve').catch((error) => {
                    logger.info('Failed to resolve qskills skill', error);
                    return undefined;
                });
                if (isCancelled()) {
                    return;
                }
                if (!skill) {
                    if (qfillValue) {
                        setUserInput(qfillValue);
                    }
                    return;
                }
                skillName = skill.name;
                localizedName = getSkillDisplayName(skill);
            }
            const prefill = buildSkillPrefill({
                beforeMention: parsedInstruction?.beforeMention ?? '',
                afterMention: parsedInstruction
                    ? parsedInstruction.afterMention
                    : qfillValue
                        ? ` ${qfillValue}`
                        : '',
                localizedName,
                skillId,
                skillName,
            });
            if (isCancelled()) {
                return;
            }
            setUserInput(prefill.value, prefill.json);
            return;
        }
        if (qfillValue) {
            setUserInput(qfillValue);
        }
    }, [$t, setUserInput]);
    const applyQSources = useCallback(async (sourcesValue, bypassCache) => {
        const sourceIds = getSourcesForQSources(sourcesValue);
        if (sourceIds.length === 0) {
            return;
        }
        const results = await Promise.all(sourceIds.map((sourceId) => fetchSource({ queryClient, sourceId, reason: 'qfill-source-resolve', bypassCache }).catch((error) => {
            logger.info(`Failed to resolve qsources source: ${sourceId}`, error);
            return null;
        })));
        const connectedSources = [];
        const unconnectedSources = [];
        for (const result of results) {
            if (!result || !result.isAllowed) {
                continue;
            }
            if (result.isConnected) {
                connectedSources.push(result.source.id);
            }
            else {
                unconnectedSources.push(result.source.id);
            }
        }
        if (connectedSources.length > 0) {
            setSources(connectedSources);
        }
        return unconnectedSources;
    }, [queryClient, setSources]);
    const applyQMode = useCallback((modeValue) => {
        const mode = getSearchModeForQMode(modeValue);
        if (!mode) {
            logger.info(`Invalid search mode in URL parameter: ${modeValue}`);
            return;
        }
        const model = getPreferredSearchModelForSearchMode(mode);
        // TODO: type cast is part of dynamic model migration
        setConfiguredModel(model);
    }, [getPreferredSearchModelForSearchMode, setConfiguredModel]);
    const applyQModel = useCallback((modelValue) => {
        const model = getSearchModelForQModel(modelValue);
        if (!model) {
            logger.info(`Invalid search model in URL parameter: ${modelValue}`);
            return;
        }
        setConfiguredModel(model);
    }, [setConfiguredModel]);
    const applyPendingPrefill = useCallback((isCancelled) => {
        if (!pathname ||
            !ASK_INPUT_PREFILL_LANDING_PATHS.some((landingPath) => landingPath === pathname)) {
            return;
        }
        const pendingPrefill = getPendingAskInputPrefill();
        if (!pendingPrefill || !isSearchModelsLoaded) {
            return;
        }
        const model = getPreferredSearchModelForSearchMode(pendingPrefill.searchMode);
        if (configuredModel !== model) {
            // TODO: type cast is part of dynamic model migration
            setConfiguredModel(model);
            return;
        }
        const prefill = takePendingAskInputPrefill();
        if (!prefill) {
            return;
        }
        if (prefill.skillIds !== undefined && prefill.skillIds.length > 0) {
            void applyQFill(prefill.value, prefill.skillIds.join(','), isCancelled);
            return;
        }
        const linkedSkillPrefill = buildSkillLinkPrefill(prefill.value);
        if (linkedSkillPrefill) {
            setUserInput(linkedSkillPrefill.value, linkedSkillPrefill.json);
            return;
        }
        const { value, json } = buildPendingAskInputPrefill(prefill);
        setUserInput(value, json);
    }, [
        applyQFill,
        configuredModel,
        getPreferredSearchModelForSearchMode,
        isSearchModelsLoaded,
        pathname,
        setConfiguredModel,
        setUserInput,
    ]);
    // Connector sample queries are composed in another route. Consume their
    // complete editor state only once the landing-page composer is ready, while
    // also covering the case where the modal was already opened over `/`.
    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }
        let isCancelled = false;
        const run = () => applyPendingPrefill(() => isCancelled);
        run();
        document.addEventListener(ASK_INPUT_PREFILL_PENDING_EVENT, run);
        return () => {
            isCancelled = true;
            document.removeEventListener(ASK_INPUT_PREFILL_PENDING_EVENT, run);
        };
    }, [applyPendingPrefill]);
    // Params that can be applied immediately, without waiting for search models to load.
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        let isCancelled = false;
        const applyImmediateParams = async () => {
            const qfillValue = query.get(QFILL_PARAM) ?? query.get(Q_PARAM);
            const skillsValue = query.get(QSKILLS_PARAM);
            const sourcesValue = query.get(QSOURCES_PARAM);
            const attachUrl = query.get(QATTACH_PARAM);
            const attachName = query.get(QATTACH_NAME_PARAM);
            const remixId = query.get(QREMIX_PARAM);
            const relatedQueryUUID = query.get(QRELATED_QUERY_UUID_PARAM);
            // Store upsell attribution for cross-page navigation (e.g. Ask -> /computer)
            if (relatedQueryUUID) {
                setQFillAttribution(relatedQueryUUID);
            }
            const hasAnyParam = qfillValue || skillsValue || sourcesValue || attachUrl || remixId;
            if (!hasAnyParam) {
                return;
            }
            if (qfillValue || skillsValue) {
                await applyQFill(qfillValue, skillsValue, () => isCancelled);
                if (isCancelled) {
                    return;
                }
            }
            let unconnectedSources = [];
            if (sourcesValue) {
                unconnectedSources =
                    (await applyQSources(sourcesValue, query.get(QSOURCES_REFRESH_PARAM) !== null)) ?? [];
                if (isCancelled) {
                    return;
                }
            }
            if (attachUrl && addAttachmentUrl && isValidHttpUrl(attachUrl)) {
                addAttachmentUrl(attachUrl, attachName ?? 'file');
                if (!qfillValue) {
                    openToast({
                        message: $t({
                            defaultMessage: 'File attached — write a prompt to get started',
                            id: '581954b98a',
                            description: 'Toast message shown when a file is attached from URL parameter',
                        }),
                        variant: 'success',
                        timeoutSeconds: 3,
                    });
                }
            }
            let shouldPreserveRemixParam = !!remixId;
            if (remixId && addRemixAttachment) {
                try {
                    shouldPreserveRemixParam = !(await addRemixAttachment(remixId));
                }
                catch {
                    shouldPreserveRemixParam = true;
                }
                if (isCancelled) {
                    return;
                }
                if (!shouldPreserveRemixParam && !qfillValue) {
                    openToast({
                        message: $t({
                            defaultMessage: 'Project attached — write a prompt to get started',
                            id: '6453a10e41',
                            description: 'Toast shown when a remix project is attached from URL params',
                        }),
                        variant: 'success',
                        timeoutSeconds: 3,
                    });
                }
            }
            if (qfillValue) {
                openToast({
                    message: $t({
                        defaultMessage: 'Your command is ready to submit',
                        id: 'ab16db12d5',
                        description: 'Toast message shown when command is prefilled from URL parameter',
                    }),
                    variant: 'success',
                    timeoutSeconds: 3,
                });
            }
            if (isCancelled) {
                return;
            }
            const update = {};
            for (const param of IMMEDIATE_SWALLOWED_PARAMS) {
                if (!addAttachmentUrl && (param === QATTACH_PARAM || param === QATTACH_NAME_PARAM)) {
                    continue;
                }
                if (shouldPreserveRemixParam && param === QREMIX_PARAM) {
                    continue;
                }
                update[param] = undefined;
            }
            if (unconnectedSources.length > 0) {
                update[QSOURCE_SUGGESTIONS_PARAM] = mergeSourceSuggestionParam(query.get(QSOURCE_SUGGESTIONS_PARAM) ?? undefined, unconnectedSources);
            }
            updateSearchParams(update);
        };
        void applyImmediateParams();
        return () => {
            isCancelled = true;
        };
    }, [query, applyQFill, applyQSources, addAttachmentUrl, addRemixAttachment, openToast, $t]);
    // Params that depend on the search models list being loaded.
    // qmode resolves a SearchMode to its preferred model, which requires the models list.
    // qmodel is applied here too so its ordering vs qmode is preserved (qmodel wins when both are present).
    useEffect(() => {
        // Home clears the previous result in a later commit. Wait for that commit so
        // ThreadConfigProvider resets the old thread before this navigation intent is
        // consumed; otherwise its split-commit reset can overwrite the requested model.
        if (typeof window === 'undefined' || !isSearchModelsLoaded || currentThreadId !== undefined) {
            return;
        }
        const modeValue = query.get(QMODE_PARAM);
        const modelValue = query.get(QMODEL_PARAM) ?? query.get(QMODEL_ID_PARAM);
        if (!modeValue && !modelValue) {
            return;
        }
        if (modeValue) {
            applyQMode(modeValue);
        }
        if (modelValue) {
            applyQModel(modelValue);
        }
        const update = {};
        for (const param of LOADED_SWALLOWED_PARAMS) {
            update[param] = undefined;
        }
        updateSearchParams(update);
    }, [query, applyQMode, applyQModel, currentThreadId, isSearchModelsLoaded]);
}
//# sourceMappingURL=useQueryFillParams.js.map